import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { AnimRig } from "./rig";
import { RigidRig } from "./rigidRig";
import { HQ_YAW, lib } from "./views/common";
import { bakeStatic } from "./views/staticBatch";
import { softDisc } from "./views/critterFlock";
import { WornSkins } from "./skins/wornSkins";
import { reducedVfx } from "../ui/settings";
import { ChimneySmoke } from "./views/chimneySmoke";
import { HubCampLayout, TroopAct } from "./hubCampLayout";

/**
 * The hub's frontier war camp (owner 2026-10-22: the home screen "looks too boring"; chose the war camp — the
 * fortress as the army's stronghold, the road out to battle, tents, banners, troops drilling and marching, campfires).
 *
 * Where everything stands is hubCampLayout.ts: grid offsets from the fortress cell and whole quarter turns, so the
 * palisade, the gate and the tents sit on the fortress's own isometric grid (2026-10-24; the first cut ran the
 * palisade flat across the screen). The stands' crescent (HubLayout.Stands) stays clear: anything that would land
 * within `Clear` of a stand, the ghost's stand or the fortress is skipped, so every line-up keeps its pads clear.
 *
 * Cost: the props without clips (tents, palisade, crates…) bake into a few merged meshes (staticBatch.ts) that cast
 * shadows; the ones that move (the campfire's flames, the banners' cloth, the rally flag) stay live, and so do the
 * troops (one rigid-rig clone each, like a level's soldiers). Under Reduced effects the troops hold their idle.
 */

interface Troop {
  readonly root: THREE.Group; readonly rig: AnimRig; readonly act: TroopAct; readonly baseYaw: number;
  readonly path: THREE.Vector2[] | null; leg: number; dir: 1 | -1; wait: number; next: number; swings: number;
}

export class HubCamp {
  /** Keep this far (cells, world) from a stand, the ghost's stand and the fortress footprint. */
  static readonly Clear = 1.0;
  /** The troops at the hub's distance read better a little larger than on a map (they stand ~30 px tall there). */
  static readonly TroopScale = 1.6;
  /** Walking speed of a patrol, cells a second, and its pause at each end. */
  static readonly PatrolSpeed = 0.55;
  static readonly PatrolPause = [1.5, 3] as const;
  /** Seconds between the drill line's blows (in step, a little staggered) and between the archers' shots. */
  static readonly DrillEvery = 1.7;
  static readonly ArcherEvery = [2.4, 3.6] as const;

  /** The camp's new art (art/camp/); a model not shipped yet is simply not placed. */
  static readonly CampModels = ["camp_tent_command", "camp_tent", "camp_palisade", "camp_watchtower", "camp_gate", "camp_weapon_rack", "camp_dummy", "camp_target", "camp_banner", "camp_cart"] as const;
  /** Older props the camp reuses. */
  static readonly PropModels = ["p_campfire", "p_crates", "p_barrel_stack"] as const;

  readonly group = new THREE.Group();
  private meshes: THREE.Mesh[] = [];
  private readonly live: {
    root: THREE.Object3D; rig: AnimRig; phase: number;
    flags: { n: THREE.Object3D; rx: number; ry: number }[]; flames: { n: THREE.Object3D; sy: number; sx: number }[];
  }[] = [];
  private readonly troops: Troop[] = [];
  /** Every troop's soft blob in one draw. */
  private shadows: THREE.InstancedMesh | null = null;
  private readonly m4 = new THREE.Matrix4();
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly shadowGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  private time = 0;
  private smoke: THREE.Points | null = null;

  /** World XZ of a grid offset from `base` (hubCampLayout.ts). */
  static toWorld(base: Cell, dx: number, dz: number): { x: number; z: number } { return HubCampLayout.toWorld(base, dx, dz); }

  /** Every model the camp may place (assetNeeds.ts loads them with the hub). */
  static assetNames(): string[] {
    const t = WornSkins.troopModel();
    return [...HubCamp.CampModels, ...HubCamp.PropModels, t, `${t}_lod1`, "troop_archer", "troop_archer_lod1"];
  }

  /** The road's cells (world), for the ground's splat. */
  static roadCells(base: Cell): Cell[] {
    const out: Cell[] = [];
    const pts = HubCampLayout.Road;
    for (let i = 0; i + 1 < pts.length; i++) {
      const [u0, v0] = pts[i], [u1, v1] = pts[i + 1];
      const n = Math.max(1, Math.ceil(Math.hypot(u1 - u0, v1 - v0) * 1.2));
      for (let k = i === 0 ? 0 : 1; k <= n; k++) {
        const t = k / n, w = HubCamp.toWorld(base, u0 + (u1 - u0) * t, v0 + (v1 - v0) * t);
        const c = new Cell(Math.round(w.x), Math.round(w.z));
        if (!out.length || !out[out.length - 1].equals(c)) out.push(c);
      }
    }
    return out;
  }
  /** The trodden yards (world), for the ground's splat. */
  static worn(base: Cell): { x: number; z: number; r: number }[] {
    return HubCampLayout.Worn.map(([dx, dz, r]) => ({ ...HubCamp.toWorld(base, dx, dz), r }));
  }
  /** Points the hub's shot frames whatever the line-up (the camp's extent), so the home shot is the same every visit. */
  static framePoints(base: Cell, y: number): THREE.Vector3[] {
    // screen-aligned (u right, v behind): the same extent as the first cut's, so the fortress keeps its size on screen
    return ([[-5.0, -2.2, 0], [5.0, -2.2, 0], [-5.4, 5.3, 2.5], [5.4, 5.3, 2.5], [0, 6.0, 1.75]] as const).map(([u, v, h]) => { const g = HubCampLayout.grid(u, v), w = HubCamp.toWorld(base, g.dx, g.dz); return new THREE.Vector3(w.x, y + h, w.z); });
  }

  constructor(private readonly base: Cell, private readonly groundY: number, keepClear: readonly Cell[], footprint: readonly Cell[]) {
    this.shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: 0.32, depthWrite: false, toneMapped: false });
    const L = lib();
    if (!L) return;
    const blocked = (x: number, z: number, r: number) =>
      keepClear.some((c) => Math.hypot(c.x - x, c.y - z) < HubCamp.Clear + r) ||
      footprint.some((c) => Math.abs(c.x - x) < 1.0 + r && Math.abs(c.y - z) < 1.0 + r);
    const radius = (name: string) => { const a = L.anchors.get(name); return a ? Math.min(0.9, Math.max(a.half_x ?? 0.3, a.half_z ?? 0.3) * 0.6) : 0.3; };
    // A prop whose clips play (the campfire's flames, the rally flag) or whose cloth and flames the camp moves itself
    // (art/camp: `flag<n>` / `flame<n>` nodes) stays live; everything else bakes.
    const scratch = new THREE.Group();
    const smokeAt: THREE.Vector3[] = [];
    for (const s of [...HubCampLayout.Props, ...HubCampLayout.LiveProps]) {
      if (!L.hq(s.name)) continue;
      const w = HubCamp.toWorld(base, s.dx, s.dz);
      if (blocked(w.x, w.z, radius(s.name))) continue;
      const root = L.clone(`hq:${s.name}`);
      root.position.set(w.x, groundY, w.z);
      root.rotation.y = HubCampLayout.yaw(s.q);
      if (s.scale) root.scale.multiplyScalar(s.scale);
      const clips = L.clipsOf(`hq:${s.name}`);
      const flags: THREE.Object3D[] = [], flames: THREE.Object3D[] = [];
      root.traverse((o) => { if (/flag\d+$/.test(o.name)) flags.push(o); else if (/flame\d+$/.test(o.name)) flames.push(o); });
      if (!clips.length && !flags.length && !flames.length) { scratch.add(root); continue; }
      // cloth and fire cast no shadow (and so cost no shadow-map draws)
      for (const n of [...flags, ...flames]) n.traverse((o) => { o.castShadow = false; });
      // smoke rises off the campfires and the braziers
      root.updateMatrixWorld(true);
      if (s.name === "p_campfire") smokeAt.push(new THREE.Vector3(w.x, groundY + 0.22, w.z));
      if (s.name === "camp_watchtower") for (const f of flames) smokeAt.push(f.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0.12, 0)));
      const rig = new AnimRig(root, clips);
      if (clips.some((c) => c.name === "idle")) rig.playLoop("idle", 1, true);
      this.group.add(root);
      const phase = (w.x * 1.7 + w.z * 2.3) % 6.28;
      this.live.push({ root, rig, flags: flags.map((n) => ({ n, rx: n.rotation.x, ry: n.rotation.y })), flames: flames.map((n) => ({ n, sy: n.scale.y, sx: n.scale.x })), phase });
    }
    this.group.add(scratch);
    this.meshes = bakeStatic(scratch, this.group, 32, { shadows: true, lod: () => ({ ratio: 0.6, error: 0.01 }) });
    this.group.remove(scratch);
    if (smokeAt.length) { this.smoke = new ChimneySmoke(smokeAt, reducedVfx() ? ChimneySmoke.PuffsReduced : ChimneySmoke.Puffs).points; this.group.add(this.smoke); }
    const soldier = WornSkins.troopModel();
    for (const t of HubCampLayout.Troops) {
      const model = t.act === "archer" ? "troop_archer" : soldier;
      if (!L.hq(model)) continue;
      const w = HubCamp.toWorld(base, t.dx, t.dz);
      if (!t.path && blocked(w.x, w.z, 0.15)) continue;
      const path = t.path ? t.path.map(([dx, dz]) => { const p = HubCamp.toWorld(base, dx, dz); return new THREE.Vector2(p.x, p.z); }) : null;
      if (path && path.some((p) => blocked(p.x, p.y, 0.1))) continue;
      const root = new THREE.Group();
      // ~40 px tall in the shot: the simplified soldier (same nodes and clips) where it ships, and no shadow-map
      // casting — each has its soft blob below
      const lod = L.hq(`${model}_lod1`) ? `${model}_lod1` : model;
      const body = L.clone(`hq:${lod}`);
      body.traverse((o) => { o.castShadow = false; });
      body.rotation.y = HQ_YAW;
      if (model !== "troop_archer") WornSkins.dressTroop(body);
      root.add(body);
      root.scale.setScalar(HubCamp.TroopScale);
      root.position.set(w.x, groundY, w.z);
      // the body is turned to face +z (HQ_YAW): the root's yaw points it at its `look`, else down its path
      const to = t.look ?? t.path?.[1] ?? [t.dx, t.dz - 1];
      const baseYaw = Math.atan2(to[0] - t.dx, to[1] - t.dz);
      root.rotation.y = baseYaw;
      const rig = new AnimRig(body, L.clipsOf(`hq:${lod}`));
      rig.playLoop(t.act === "patrol" ? "walk" : "idle", 1, true);
      this.group.add(root);
      this.troops.push({ root, rig, act: t.act, baseYaw, path, leg: 0, dir: 1, wait: 0, next: Math.random() * 2, swings: 0 });
    }
    if (this.troops.length) {
      this.shadows = new THREE.InstancedMesh(this.shadowGeo, this.shadowMat, this.troops.length);
      this.shadows.renderOrder = 1;
      this.shadows.frustumCulled = false;
      this.group.add(this.shadows);
      this.placeShadows();
    }
  }

  private placeShadows(): void {
    const sh = this.shadows;
    if (!sh) return;
    const k = 0.42 * HubCamp.TroopScale;
    this.troops.forEach((t, i) => { this.m4.makeScale(k, 1, k).setPosition(t.root.position.x, this.groundY + 0.012, t.root.position.z); sh.setMatrixAt(i, this.m4); });
    sh.instanceMatrix.needsUpdate = true;
  }

  /** How many troops and live props run clips (the hub tells the shadow rig how many casters animate). */
  get animated(): number { return this.live.length; }

  update(dt: number): void {
    this.time += dt;
    const still = reducedVfx(), t0 = this.time;
    for (const p of this.live) {
      p.rig.update(dt);
      if (still) continue;
      // cloth sways from its hoist, a flame licks up and down: cheap transforms on the art's own nodes
      for (const f of p.flags) { const k = t0 * 2.1 + p.phase; f.n.rotation.y = f.ry + 0.16 * Math.sin(k) + 0.05 * Math.sin(k * 2.7); f.n.rotation.x = f.rx + 0.05 * Math.sin(k * 1.3 + 1); }
      for (const f of p.flames) { const k = t0 * 9 + p.phase * 3; const j = 0.12 * Math.sin(k) + 0.07 * Math.sin(k * 2.3 + 1.1); f.n.scale.y = f.sy * (1 + j); f.n.scale.x = f.sx * (1 - j * 0.4); }
    }
    for (let i = 0; i < this.troops.length; i++) {
      const t = this.troops[i];
      if (!still) this.act(t, i, dt);
      t.rig.update(dt);
    }
    if (!still && this.troops.some((t) => t.act === "patrol")) this.placeShadows();
  }

  private act(t: Troop, i: number, dt: number): void {
    switch (t.act) {
      case "drill": {
        // the line swings together, each a beat behind its neighbour
        const beat = Math.floor((this.time - i * 0.12) / HubCamp.DrillEvery);
        if (beat > t.swings) { t.swings = beat; t.rig.playOnce(beat % 2 ? "attack2" : "attack", false, null, { solo: true }); }
        return;
      }
      case "archer":
        if ((t.next -= dt) <= 0) { t.rig.playOnce("attack", false, null, { solo: true }); t.next = this.between(HubCamp.ArcherEvery); }
        return;
      case "sergeant": case "rest": case "watch":
        if ((t.next -= dt) <= 0) {
          t.rig.playOnce(t.act === "sergeant" && Math.random() < 0.6 ? "cheer" : "fidget", false, null, { solo: true });
          t.next = t.act === "sergeant" ? this.between([3, 6]) : this.between([6, 14]);
        }
        return;
      case "patrol": {
        const path = t.path!;
        if (t.wait > 0) {
          if ((t.wait -= dt) <= 0) t.rig.playLoop("walk");
          return;
        }
        const target = path[t.leg + (t.dir > 0 ? 1 : 0)] ?? path[path.length - 1];
        const p = t.root.position, dx = target.x - p.x, dz = target.y - p.z, d = Math.hypot(dx, dz);
        const step = HubCamp.PatrolSpeed * dt;
        // turn toward the way it walks, eased
        const want = Math.atan2(dx, dz);
        let diff = want - t.root.rotation.y; diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        t.root.rotation.y += diff * Math.min(1, dt * 8);
        if (d <= step) {
          p.x = target.x; p.z = target.y;
          const end = t.dir > 0 ? t.leg + 1 >= path.length - 1 : t.leg <= 0;
          if (end) { t.dir = t.dir > 0 ? -1 : 1; t.wait = this.between(HubCamp.PatrolPause); t.rig.playLoop("idle"); }
          else t.leg += t.dir;
          return;
        }
        p.x += (dx / d) * step; p.z += (dz / d) * step;
        return;
      }
    }
  }

  private between(r: readonly [number, number]): number { return r[0] + Math.random() * (r[1] - r[0]); }

  dispose(): void {
    for (const m of this.meshes) { m.removeFromParent(); m.geometry.dispose(); }
    this.meshes.length = 0;
    for (const p of this.live) RigidRig.dispose(p.root);
    for (const t of this.troops) RigidRig.dispose(t.root);
    this.smoke?.geometry.dispose();
    this.shadowGeo.dispose();
    this.shadowMat.map?.dispose();
    this.shadowMat.dispose();
    this.group.removeFromParent();
  }
}
